/**
 * Runtime static-prop model (#816) — parse a prop GLB, normalise it to a unit
 * column, and instantiate it at a map-data-derived footprint + height.
 *
 * This is the props counterpart of `characterModel.ts`, and deliberately the
 * SAME shape: a GPU-free module that parses a GLB `ArrayBuffer` into a Three
 * scene graph and does the (pure) placement math, so both can be unit-tested
 * headless (jsdom / node GLTF parse) while `threeLayer.ts` owns the renderer.
 * A prop has no skeleton, no clips and no facing, so there is no mixer, no
 * clip lookup and no per-frame reconcile here — `threeLayer.setProps` replaces
 * the whole prop set at arena load.
 *
 * THE LOAD-BEARING INVARIANT (bible §7 "collision circle = visual footprint",
 * the 3D half of `arena.ts`'s 2D one): the instantiated prop's world-space BASE
 * RADIUS equals the map's collision `radius` EXACTLY. A pillar that looks 5%
 * wider than it blocks is a gameplay bug, so the model is first normalised to a
 * unit column (circumscribing horizontal radius 1, height 1, base at y = 0) and
 * then scaled by `radiusUnits` horizontally — never by the GLB's own baked
 * export scale, and never by a hand-tuned per-model number.
 *
 * Height and footprint are scaled INDEPENDENTLY (a prop's height rule comes
 * from `props.ts`'s `heightDiameters` × the map radius — see `propHeightUnits`),
 * so a replacement GLB with different proportions still registers exactly and
 * still stands exactly as tall as the rule says. That is a deliberate
 * non-uniform scale: the alternative (uniform scale) would let the art's own
 * aspect ratio silently decide either the footprint or the height.
 *
 * MATERIALS ARE NEVER TOUCHED. The shipped pillar GLB carries its own baked
 * texture; applying a flat in-engine material here would override it.
 *
 * FAIL LOUDLY, NEVER DEGRADE (#829, owner decision Q2). This module used to
 * resolve `null` for a missing / malformed / mesh-less GLB so the caller could
 * fall back to the 2D Pixi pillar sprite. #829 deleted that sprite: 3D props
 * are now the ONLY pillar path, so "resolve null" would mean pillars that are
 * INVISIBLE but still block movement and LoS — the worst failure this renderer
 * can have, and exactly how a Git LFS pointer file ships to production. Both
 * entry points therefore REJECT with a typed {@link PropAssetError} carrying an
 * actionable message. There is no fallback to preserve, and no silent path out.
 *
 * PRESENTATION ONLY — never reads or writes sim state or any hash.
 */

import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { hasMesh } from "./characterModel.js";
import { simToWorld3 } from "./isoCamera.js";

/**
 * Why a prop's GLB could not be used. A closed union so every failure has its
 * own actionable message and a test can assert WHICH one fired (a 404 and a
 * pointer file are very different bugs with very different fixes).
 */
export type PropAssetFailure =
  /** The asset request completed with a non-OK status (typically 404). */
  | { readonly kind: "http"; readonly status: number }
  /** The request itself failed (offline, DNS, CORS, aborted). */
  | { readonly kind: "network" }
  /** The bytes are a Git LFS POINTER FILE, not a GLB — the `.gitattributes`
   *  failure mode: a checkout without LFS yields ~130 bytes of text that looks
   *  like a present file and is not. */
  | { readonly kind: "lfs-pointer" }
  /** The bytes are not a parseable GLB (truncated, corrupt, wrong format). */
  | { readonly kind: "parse" }
  /** A spec-valid GLB that carries no mesh (the "exported the rig, not the
   *  mesh" case) — it would instantiate as a perfectly invisible column. */
  | { readonly kind: "no-mesh" };

/** Human-readable, ACTIONABLE text per failure. Written for whoever sees this
 *  in a console at 2am, not for a stack trace. */
function describeFailure(failure: PropAssetFailure): string {
  switch (failure.kind) {
    case "http":
      return `request failed with HTTP ${String(failure.status)} — the file is not being served`;
    case "network":
      return "the request itself failed (offline / blocked / aborted)";
    case "lfs-pointer":
      return (
        "the bytes are a Git LFS POINTER FILE, not a GLB — run `git lfs install && git lfs pull`" +
        " (CI: `actions/checkout` needs `lfs: true`)"
      );
    case "parse":
      return "the bytes are not a parseable GLB (truncated / corrupt / wrong format)";
    case "no-mesh":
      return "the GLB parsed but carries NO MESH — it would render as an invisible column";
  }
}

/**
 * A prop's 3D model is unusable. Thrown — never swallowed — because 3D props
 * have no fallback path (#829): the alternative to failing here is an invisible
 * pillar that still blocks movement and line of sight.
 */
export class PropAssetError extends Error {
  /** Which failure this is, for callers/tests that branch on it. */
  readonly failure: PropAssetFailure;
  /** The asset the failure is about (URL, or a test-supplied label). */
  readonly assetUrl: string;

  constructor(assetUrl: string, failure: PropAssetFailure, options?: ErrorOptions) {
    super(
      `3D prop model "${assetUrl}" is unusable: ${describeFailure(failure)}. ` +
        "3D props are the only pillar path (#829) — there is no 2D fallback.",
      options,
    );
    this.name = "PropAssetError";
    this.failure = failure;
    this.assetUrl = assetUrl;
  }
}

/** The first bytes of every Git LFS pointer file (`.gitattributes`). */
const LFS_POINTER_PREFIX = "version https://git-lfs.github.com/spec/v1";

/**
 * Does `data` look like a Git LFS pointer rather than a GLB? Checked BEFORE
 * parsing purely so the error names the real problem: a pointer file would
 * otherwise surface as a generic "not a parseable GLB", sending the reader
 * hunting for a corrupt export instead of running `git lfs pull`.
 */
function isLfsPointer(data: ArrayBuffer): boolean {
  // Pointer files are ~130 bytes; anything larger is a real (possibly bad) GLB.
  if (data.byteLength > 1024 || data.byteLength < LFS_POINTER_PREFIX.length) return false;
  const head = new Uint8Array(data, 0, LFS_POINTER_PREFIX.length);
  return new TextDecoder().decode(head) === LFS_POINTER_PREFIX;
}

/** A parsed, placement-ready static prop model. */
export interface LoadedPropModel {
  /**
   * The placement handle. Already normalised to a UNIT COLUMN: circumscribing
   * horizontal radius 1, height 1, base at local y = 0, centred on x/z. The
   * caller sets `.position` (world) and `.scale` (footprint radius / height)
   * on a CLONE of this node — see {@link instantiateProp}.
   */
  readonly root: THREE.Group;
}

/** Where + how big one prop instance stands, in SIM units. */
export interface PropPlacement {
  /** Prop centre on the sim ground plane (map data). */
  readonly wx: number;
  readonly wy: number;
  /** The COLLISION radius from map data. The instance's base radius == this. */
  readonly radiusUnits: number;
  /** Height above the floor, derived from map data by `propHeightUnits`. */
  readonly heightUnits: number;
}

/**
 * Circumscribing horizontal radius of a bounding-box size: half of the LARGER
 * of the two ground-plane extents. Conservative on purpose — registering the
 * widest horizontal extent against the collision radius means the drawn prop
 * can never be wider than the circle it blocks (the narrower axis reads
 * slightly inside it, which is the safe direction). Pure.
 */
export function horizontalRadiusOf(size: THREE.Vector3): number {
  return Math.max(size.x, size.z) / 2;
}

/**
 * Positive-scale guard: a non-positive / non-finite factor would collapse,
 * mirror or NaN the model, so it degrades to the no-op scale of 1 — the same
 * discipline `computeUniformScale` uses for a degenerate character height.
 */
function safeScale(factor: number): number {
  return factor > 0 && Number.isFinite(factor) ? factor : 1;
}

/**
 * Normalise `scene` in place and wrap it in a placement handle: scaled so its
 * circumscribing horizontal radius is 1 and its height is 1, dropped so its
 * base sits at local y = 0, and centred on x/z. The returned handle's `.scale`
 * is then, by construction, `(footprintRadius, height, footprintRadius)` in sim
 * units — which is what makes the registration guarantee exact.
 *
 * The box is measured with `precise` vertex sampling (like
 * `normaliseToPlacementRoot`) so a GLB whose nodes carry their own transforms
 * is measured as it actually renders.
 */
export function normalisePropToUnitColumn(scene: THREE.Object3D): THREE.Group {
  scene.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(scene, true);
  const size = new THREE.Vector3();
  const centre = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(centre);

  const horizontal = safeScale(1 / horizontalRadiusOf(size));
  const vertical = safeScale(1 / size.y);
  scene.scale.set(
    scene.scale.x * horizontal,
    scene.scale.y * vertical,
    scene.scale.z * horizontal,
  );
  // After scaling about the origin the box corners scale too: shift so the
  // (scaled) base touches y = 0 and the column is centred on x/z.
  scene.position.set(-centre.x * horizontal, -box.min.y * vertical, -centre.z * horizontal);

  const root = new THREE.Group();
  root.name = "PropRoot";
  root.add(scene);
  return root;
}

/**
 * Clone `model` and place one instance: centred at the placement's sim ground
 * position, base on the floor, base radius == `radiusUnits` EXACTLY, height ==
 * `heightUnits`. `Object3D.clone()` (not `SkeletonUtils.clone`) is correct here
 * — a prop has no skeleton — and shares geometry/material across instances, so
 * N pillars cost one upload.
 */
export function instantiateProp(model: LoadedPropModel, placement: PropPlacement): THREE.Object3D {
  const root = model.root.clone(true);
  root.position.copy(simToWorld3(placement.wx, placement.wy, 0));
  const r = safeScale(placement.radiusUnits);
  root.scale.set(r, safeScale(placement.heightUnits), r);
  return root;
}

/**
 * Parse a static prop GLB from raw bytes into a placement-ready model.
 *
 * REJECTS with a {@link PropAssetError} — never resolves `null`, never resolves
 * a mesh-less model — on an LFS pointer file, malformed bytes, or a scene with
 * no mesh (#829). `assetUrl` is only used to name the asset in the message.
 */
export async function parsePropModel(
  data: ArrayBuffer,
  assetUrl: string,
): Promise<LoadedPropModel> {
  if (isLfsPointer(data)) throw new PropAssetError(assetUrl, { kind: "lfs-pointer" });
  const loader = new GLTFLoader();
  let gltf: { scene: THREE.Group };
  try {
    gltf = await new Promise<{ scene: THREE.Group }>((resolve, reject) => {
      loader.parse(
        data,
        "",
        (result) => {
          resolve(result);
        },
        (error: unknown) => {
          reject(error instanceof Error ? error : new Error(String(error)));
        },
      );
    });
  } catch (cause) {
    throw new PropAssetError(assetUrl, { kind: "parse" }, { cause });
  }
  if (!hasMesh(gltf.scene)) throw new PropAssetError(assetUrl, { kind: "no-mesh" });
  return { root: normalisePropToUnitColumn(gltf.scene) };
}

/**
 * Fetch + parse a prop GLB from `assetUrl`. The one production entry point
 * (`mount.ts`), and the reason the fetch lives here rather than inline in the
 * renderer: every way this can fail — HTTP status, network, pointer file,
 * corrupt bytes, mesh-less scene — becomes the SAME typed
 * {@link PropAssetError}, so the caller has exactly one thing to surface and a
 * test can drive every branch without a renderer.
 */
export async function loadPropModel(assetUrl: string): Promise<LoadedPropModel> {
  let res: Response;
  try {
    res = await fetch(assetUrl);
  } catch (cause) {
    throw new PropAssetError(assetUrl, { kind: "network" }, { cause });
  }
  if (!res.ok) throw new PropAssetError(assetUrl, { kind: "http", status: res.status });
  return parsePropModel(await res.arrayBuffer(), assetUrl);
}
